import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { PlainModal } from '../Modal/Modal';
import { useSetExclusive } from './useSetExclusive';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { PriceInput } from '../PriceInput/PriceInput';
import { Price, UpdatedPrice } from './types';
import { PriceForm } from './PriceForm';

type ConvertExclusiveModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onConvertSuccess: (price: Price) => void;
  influencerId: number;
  isExclusive: boolean;
  updatedPrice: UpdatedPrice;
  subscriptionMonthlyPrice: number | null;
  subscriptionAnnualPrice: number | null;
};

export const ConvertExclusiveModal = ({
  influencerId,
  isOpen,
  onClose,
  onConvertSuccess,
  isExclusive,
  updatedPrice,
  subscriptionMonthlyPrice,
  subscriptionAnnualPrice,
}: ConvertExclusiveModalProps) => {
  const {
    icon,
    title,
    description,
    cta,
    priceForm,
    priceInput,
    priceError,
    isSubmitting,
    selectedType,
    selectType,
  } = useSetExclusive({
    influencerId,
    onConvertSuccess,
    onClose,
    isExclusive,
    updatedPrice,
    subscriptionMonthlyPrice,
    subscriptionAnnualPrice,
  });

  return (
    <PlainModal
      isOpen={isOpen}
      onClose={onClose}
      disableBackdropClick={true}
      sx={{
        width: 600,
        height: 'auto',
        maxWidth: 600,
        maxHeight: 'fit-content',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          gap: '16px',
          padding: '32px 32px 24px 32px',
        }}
      >
        {icon ? (
          <Box className="w-auto h-auto flex justify-center items-center">
            <FontAwesomeIcon
              icon={icon}
              className="text-orange-1 w-[32px] h-[32px] text-center"
            />
          </Box>
        ) : null}
        <Typography variant="h4">{title}</Typography>
      </Box>
      <Typography
        variant="subtitle1"
        sx={{ padding: '8px 32px' }}
        dangerouslySetInnerHTML={{ __html: `${description}` }}
      ></Typography>
      {priceForm ? (
        <PriceForm
          isSubmitting={isSubmitting}
          errors={priceError}
          selectedType={selectedType}
          selectType={selectType}
          formData={priceForm}
        />
      ) : null}
      {priceInput ? (
        <Box
          sx={{
            padding: '10px 32px',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Typography sx={{ paddingBottom: '4px', fontWeight: 600 }}>
            {priceInput.inputLabel}
          </Typography>
          <PriceInput
            disabled={isSubmitting}
            error={priceError[priceInput.key]}
            onChange={priceInput.onChange}
            placeholder="Please enter"
          />
        </Box>
      ) : null}
      {priceError?.general ? (
        <Typography
          sx={{ padding: '10px 32px', fontSize: 12, color: '#F04438' }}
        >
          {priceError?.general}
        </Typography>
      ) : null}
      <Box
        sx={{
          padding: '24px 32px 32px 32px',
          display: 'flex',
          justifyContent: 'end',
          gap: '16px',
        }}
      >
        {cta.map(({ onClick, title, className, hasLoadingState }) => (
          <Button
            key={title}
            onClick={onClick}
            className={className}
            disabled={isSubmitting}
          >
            {isSubmitting && hasLoadingState ? (
              <CircularProgress className="w-6 h-6" />
            ) : (
              title
            )}
          </Button>
        ))}
      </Box>
    </PlainModal>
  );
};
